import {useTranslation} from "react-i18next";
import {useStateContext} from "../../contexts/ContextProvider";
import {useEffectiveFacilityId} from "../../api/effectiveFacility";
import {BuildingOffice2Icon, ChevronUpDownIcon} from "@heroicons/react/24/outline";

interface FacilitySwitcherProps {
    isCompactMode: boolean;
    onClick: () => void;
}

export default function FacilitySwitcher({isCompactMode, onClick}: FacilitySwitcherProps) {
    const {currentFacility, user} = useStateContext();
    const {t} = useTranslation('navigation');
    const effectiveFacilityId = useEffectiveFacilityId();

    // Admin de groupe (facility d'origine = groupe) ou super admin, dans les
    // deux modes. is_group peut valoir 1 ou true selon l'API, d'où Boolean().
    const isOnGroupFacility = Boolean(user?.facility?.is_group);
    const isSuperAdmin = Boolean(user?.is_super_admin);

    if (!currentFacility || (!isOnGroupFacility && !isSuperAdmin)) {
        return null;
    }

    // Pastille « Vue client » : on se fie à l'établissement que le backend a
    // réellement appliqué (repli sur le sien si le client a été supprimé).
    const isClientView = isSuperAdmin
        && effectiveFacilityId !== null
        && effectiveFacilityId !== user.facility_id;

    return (
        <div className="px-2 py-3">
            <button
                onClick={onClick}
                title={isCompactMode ? currentFacility.name : undefined}
                className={`relative w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-gray-700 ${
                    isClientView ? 'bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-300' : 'bg-gray-100 hover:bg-gray-200'
                } ${isCompactMode ? 'justify-center' : ''}`}
            >
                <BuildingOffice2Icon className={`h-5 w-5 flex-shrink-0 ${isClientView ? 'text-amber-600' : 'text-gray-500'}`}/>
                {isCompactMode && isClientView && (
                    <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber-500"/>
                )}
                {!isCompactMode && (
                    <div className="flex-1 min-w-0 text-left">
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                            {t('facility_switcher.label')}
                            {isClientView && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium whitespace-nowrap">
                                    {t('facility_switcher.client_view_badge')}
                                </span>
                            )}
                        </p>
                        <p className="text-sm font-medium truncate">{currentFacility.name}</p>
                    </div>
                )}
                {!isCompactMode && (
                    <ChevronUpDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0"/>
                )}
            </button>
        </div>
    );
}
